import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown, ChevronLeft, ChevronRight, Download, GanttChartSquare, ListTree, SlidersHorizontal, ThumbsDown, ThumbsUp,
} from "lucide-react";
import { toast } from "sonner";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { PageHeader, RemovableChip, SearchInput } from "@/shared/components/ui-kit";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/shared/components/ui/resizable";
import { TooltipProvider } from "@/shared/components/ui/tooltip";
import { agentMood } from "@/shared/lib/agent-mood";
import { cn } from "@/shared/lib/cn";
import { fmtMs, fmtTokens, fmtCost } from "@/shared/lib/runs";
import { useViewport } from "@/shared/hooks/useViewport";
import BottomSheet from "@/shared/components/responsive/BottomSheet";
import {
  findSpan, flatten, SOURCE_META, traceId,
  traceAnalytics, exportTrace,
  deadAir, totalDeadAir, effectLedger,
} from "./traceModel";
import { chipCls, CopyId, DurChip, TierChip, KindIcon, StatusDot, StatusPill } from "./traces/atoms";
import { BAR_TONES, DENSITY, densityLevel, kindOf, passesDensity, toneFor } from "@/shared/lib/trace-kinds";
import { blockedMeta } from "./traces/status";
import { Metric, MetricRule } from "./traces/Metric";
import { exactUsd } from "./traces/money";
import { Segmented } from "@/shared/components/teams-ui";
import StepPanel from "./traces/StepPanel";

import type { ReactNode, RefObject } from "react";

import type { Agent, ApiVote, DensityId, Run, RunRecipe } from "@/shared/types";
import type { BillableSpan, DeadAirGap, EffectEntry, Span, SpanNote } from "./types";
import type { PanelPrefs, TraceOverview } from "./traces/stepBodies";

// Does a span match the in-trace filter (search box or a clicked chip)?
export const spanMatches = (s: Span, q: string): boolean => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return [s.name, s.label, s.chip, s.raw?.tool, s.raw?.model].some((v) => v && String(v).toLowerCase().includes(needle));
};

/* Atoms live in ./traces/atoms.jsx so the per-kind views can share them.
   Re-exported here because they were part of this module's surface first. */
export { chipCls, CopyId, DurChip };

/* -------------------------------- tree view -------------------------------- */

function Collapser({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <span
      role="button"
      tabIndex={-1}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className="press shrink-0 w-4 h-4 -ml-0.5 rounded-[4px] flex items-center justify-center text-[var(--text-quaternary)] hover:text-[var(--text-primary)]"
    >
      <ChevronDown className={`w-3 h-3 transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={2} />
    </span>
  );
}

function TreeRowShell({ span, selected, onSelect, children, dim = false }: {
  span: Span;
  selected: string;
  onSelect: (id: string) => void;
  children?: ReactNode;
  dim?: boolean;
}) {
  const isSel = selected === span.id;
  return (
    <button
      data-span-id={span.id}
      onClick={() => onSelect(span.id)}
      className={`press w-full flex items-center gap-2 pl-1.5 pr-2 h-9 rounded-[8px] text-left ${dim ? "opacity-35" : ""} ${
        isSel ? "bg-[var(--accent-active)]" : "hover:bg-[var(--accent-hover)]"
      }`}
    >
      {children}
    </button>
  );
}

/* Steps hidden by the density setting are counted, never silently dropped —
   a trace that quietly omits work is worse than one that shows too much. */
function HiddenRow({ n, onShow }: { n: number; onShow: () => void }) {
  return (
    <button
      onClick={onShow}
      className="press w-full flex items-center gap-2 pl-1.5 pr-2 h-7 rounded-[8px] text-left text-[var(--text-quaternary)] hover:text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]"
    >
      <span className="text-[11.5px]">{n} more step{n === 1 ? "" : "s"} at this density</span>
    </button>
  );
}

/** What every node in the tree needs to draw itself and its children. */
interface TreeProps {
  selected: string;
  onSelect: (id: string) => void;
  filter: string;
  onFilter?: (q: string) => void;
  level: number;
  onLevelUp: () => void;
}

function TreeBranch({ last = false, compact = false }: { last?: boolean; compact?: boolean }) {
  // Half-pixel centers keep a 1px SVG stroke aligned with the row's pixel grid.
  const bend = compact ? 5.5 : 9.5;
  return (
    <svg aria-hidden="true" width="14" className="absolute -left-[17px] top-0 h-full pointer-events-none text-[var(--text-tertiary)]" fill="none" stroke="currentColor" strokeWidth="1">
      <path d={`M0.5 0V${bend}a8 8 0 0 0 8 8H14`} />
      {!last && <line x1="0.5" x2="0.5" y1={bend} y2="100%" />}
    </svg>
  );
}

function TreeChildren({ spans, selected, onSelect, filter, onFilter, level, onLevelUp }: TreeProps & { spans: Span[] }) {
  const shown = spans.filter((s) => passesDensity(s, level));
  const hidden = spans.length - shown.length;
  return (
    <div className="relative ml-[13px] pl-[18px]">
      {shown.map((c, i) => (
        <div key={c.id} className="relative">
          <TreeBranch last={i === shown.length - 1 && hidden === 0} />
          <TreeNode span={c} selected={selected} onSelect={onSelect} filter={filter} onFilter={onFilter} level={level} onLevelUp={onLevelUp} />
        </div>
      ))}
      {hidden > 0 && (
        <div className="relative">
          <TreeBranch last compact />
          <HiddenRow n={hidden} onShow={onLevelUp} />
        </div>
      )}
    </div>
  );
}

function TreeNode({ span, selected, onSelect, filter, onFilter, level, onLevelUp }: TreeProps & { span: Span }) {
  const [open, setOpen] = useState(true);
  const hasKids = span.children.length > 0;
  const showLabel = span.label && span.label !== span.name;
  const dim = Boolean(filter && !spanMatches(span, filter) && !span.children.some((c) => spanMatches(c, filter)));
  return (
    <div>
      <TreeRowShell span={span} selected={selected} onSelect={onSelect} dim={dim}>
        {hasKids && <Collapser open={open} onToggle={() => setOpen((v) => !v)} />}
        <KindIcon kind={span.kind} status={span.status} tool={span.raw?.tool} />
        <span className={`font-mono text-[13px] text-[var(--text-primary)] truncate ${span.kind === "turn" ? "font-medium" : ""}`}>
          {span.name}
        </span>
        {span.chip && (
          <span
            role="button"
            tabIndex={-1}
            title={`Filter by ${span.chip}`}
            // Rendered only when the span HAS a chip — the guard is one line up.
            onClick={(e) => { e.stopPropagation(); onFilter?.(span.chip as string); }}
            className={`${chipCls} hidden md:inline-flex hover:ring-[var(--hairline-focus)] hover:text-[var(--text-primary)] cursor-pointer`}
          >
            {span.chip}
          </span>
        )}
        {span.tier && <TierChip tier={span.tier} className="hidden md:inline-flex" />}
        {/* Rows keep one fixed height for the branch lines, so on a phone the description lives only in the step's details */}
        {showLabel ? (
          <>
            <span className="hidden sm:block min-w-0 flex-1 text-[12px] text-[var(--text-tertiary)] truncate">{span.label}</span>
            <span className="sm:hidden flex-1" />
          </>
        ) : (
          <span className="flex-1" />
        )}
        <StatusDot status={span.status} />
        <DurChip ms={span.dur} />
      </TreeRowShell>
      {hasKids && open && (
        <TreeChildren spans={span.children} selected={selected} onSelect={onSelect} filter={filter} onFilter={onFilter} level={level} onLevelUp={onLevelUp} />
      )}
    </div>
  );
}

function TreeView({ root, agent, selected, onSelect, filter, onFilter, level, onLevelUp }: TreeProps & { root: Span; agent: Agent }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="px-4 py-3">
      <TreeRowShell span={root} selected={selected} onSelect={onSelect}>
        <Collapser open={open} onToggle={() => setOpen((v) => !v)} />
        <PixelAvatar seed={agent.seed} hue={agent.hue} size={24} ring rounded={7} emotion={agentMood(agent)} />
        <span className="font-mono text-[13.5px] font-medium text-[var(--text-primary)] truncate">{root.name}</span>
        {/* On a phone the run's title has its own line above the tree */}
        <span className="hidden sm:block min-w-0 flex-1 text-[12px] text-[var(--text-tertiary)] truncate">{root.label}</span>
        <span className="sm:hidden flex-1" />
        <DurChip ms={root.dur} />
      </TreeRowShell>
      {open && (
        <TreeChildren spans={root.children} selected={selected} onSelect={onSelect} filter={filter} onFilter={onFilter} level={level} onLevelUp={onLevelUp} />
      )}
    </div>
  );
}

/* ------------------------------ waterfall view ----------------------------- */

/* The waterfall is two columns, not one. Names live in a fixed gutter and the
   track carries nothing but bars — the older version floated each label over
   the timeline, which meant long names collided with their own bars, short
   bars pushed their labels into the next column, and the eye had no straight
   edge to read down. One gutter fixes all three. */
const WF_COLS = "grid-cols-[132px_1fr] sm:grid-cols-[190px_1fr] lg:grid-cols-[236px_1fr]";
const WF_ROW_H = 30;

const pct = (v: number, total: number): string => `${(v / total) * 100}%`;

/* Axis label. The first and last ticks hug their edges instead of centring, so
   nothing is clipped by the container at either end. */
function Tick({ t, total }: { t: number; total: number }) {
  const edge = t === 0 ? "translate-x-0" : t === 1 ? "-translate-x-full" : "-translate-x-1/2";
  return (
    <span
      className={`absolute top-0 whitespace-nowrap font-mono text-[10.5px] tabular-nums text-[var(--text-quaternary)] ${edge}`}
      style={{ left: `${t * 100}%` }}
    >
      {fmtMs(Math.round(total * t))}
    </span>
  );
}

/* One-line overview of the whole run, drawn once above the rows: every leaf in
   its own tone, and the wall-clock nobody was working in amber between them.
   It answers "what shape was this run" before any row is read, and it means
   the idle time no longer has to be striped across every row below to be
   visible. */
function SummaryLane({ root, gaps, total }: { root: Span; gaps: DeadAirGap[]; total: number }) {
  const leaves = useMemo(
    () => flatten(root).map((r) => r.span).filter((s) => s.children.length === 0),
    [root]
  );
  return (
    <span className="relative block h-[5px] rounded-full bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] overflow-hidden">
      {gaps.map((g, i) => (
        <span key={`g${i}`} className="absolute inset-y-0 bg-[var(--warn-ring)]" style={{ left: pct(g.start, total), width: pct(g.dur, total) }} />
      ))}
      {leaves.map((s) => (
        <span
          key={s.id}
          className={`absolute inset-y-0 ${
            toneFor(s.kind, s.status) === "info" ? "bg-[var(--info)]"
            : toneFor(s.kind, s.status) === "danger" ? "bg-[var(--danger)]"
            : toneFor(s.kind, s.status) === "warn" ? "bg-[var(--warn)]"
            : toneFor(s.kind, s.status) === "success" ? "bg-[var(--success)]"
            : "bg-[var(--text-quaternary)]"
          }`}
          style={{ left: pct(s.start, total), width: `max(2px, ${pct(s.dur, total)})` }}
        />
      ))}
    </span>
  );
}

/* A group (the run, a turn, a subagent) gets a slim bracket rather than a solid
   bar: it is a span of time that contains work, not work itself, and drawing it
   the same weight as its children flattens the hierarchy the tree just spent
   effort establishing. */
function GroupBracket({ span, total }: { span: Span; total: number }) {
  return (
    <span
      className="absolute top-1/2 -translate-y-1/2 h-[5px] rounded-full bg-[var(--surface-2)] ring-1 ring-[var(--hairline-strong)]"
      style={{ left: pct(span.start, total), width: `max(4px, ${pct(span.dur, total)})` }}
    />
  );
}

function Bar({ span, total, isSel }: { span: Span; total: number; isSel: boolean }) {
  const w = (span.dur / total) * 100;
  const tone = BAR_TONES[toneFor(span.kind, span.status)];
  // Sub-percent spans would render as an invisible sliver; give them a dot so
  // they stay findable and clickable at any zoom.
  if (w < 0.5) {
    return (
      <span
        title={`${span.name} · ${fmtMs(span.dur)}`}
        className={`absolute top-1/2 -translate-y-1/2 w-[6px] h-[6px] rounded-full ring-1 ${tone} ${isSel ? "ring-[1.5px]" : ""}`}
        style={{ left: pct(span.start, total) }}
      />
    );
  }
  return (
    <span
      title={`${span.name} · ${fmtMs(span.dur)}`}
      className={`absolute top-1/2 -translate-y-1/2 h-[16px] rounded-[4px] ring-1 overflow-hidden ${tone} ${
        isSel ? "ring-[1.5px] ring-[var(--hairline-focus)]" : ""
      }`}
      style={{ left: pct(span.start, total), width: `max(4px, ${w}%)` }}
    >
      {/* Prefill wait — the darker head of a generation bar is time before the
          first token, which is latency the prompt caused rather than the model. */}
      {span.kind === "llm" && span.ttft != null && (
        <span
          className="absolute inset-y-0 left-0 bg-[var(--info-ring)]"
          title={`TTFT ${fmtMs(span.ttft)}`}
          style={{ width: `${Math.min(100, (span.ttft / span.dur) * 100)}%` }}
        />
      )}
    </span>
  );
}

/* The wait immediately before a step, drawn on that step's own row as a dashed
   amber rule running into its bar. Attaching the gap to the row it delayed says
   "this step waited" — a full-height column across every row only said "time
   passed somewhere", and striped the whole chart to do it. */
function GapRule({ gap, span, total }: { gap: DeadAirGap; span: Span; total: number }) {
  return (
    <span
      title={`Waited ${fmtMs(gap.dur)} before ${span.name}`}
      className="absolute top-1/2 -translate-y-1/2 border-t border-dashed border-[var(--warn-ring)] pointer-events-none"
      style={{ left: pct(gap.start, total), width: pct(gap.dur, total) }}
    />
  );
}

function LegendKey({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2.5 h-2.5 rounded-[3px] shrink-0 ${className}`} />
      <span className="text-[11px] text-[var(--text-tertiary)]">{label}</span>
    </span>
  );
}

function Waterfall({ root, agent, selected, onSelect, filter, level }: {
  root: Span;
  agent: Agent;
  selected: string;
  onSelect: (id: string) => void;
  filter: string;
  level: number;
}) {
  const rows = useMemo(
    () => flatten(root).filter(({ span }) => passesDensity(span, level)),
    [root, level]
  );
  /* Dead air: wall-clock between steps. A waterfall that draws only the bars
     implies the space between them is instant; it is usually queueing, backoff,
     or a human not answering. Amber, because in this legend amber means
     waiting. */
  const gaps = useMemo(() => deadAir(root), [root]);
  // gap → the span it delayed, so each wait renders on the row it belongs to.
  const gapBefore = useMemo(() => new Map(gaps.filter((g) => g.before).map((g) => [g.before!.id, g])), [gaps]);
  const total = Math.max(root.dur, 1);
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const idle = gaps.reduce((n, g) => n + g.dur, 0);

  return (
    <div className="px-4 py-3">
      {/* No overflow-hidden on the card: an ancestor that clips becomes the
          scrollport for position:sticky, which pins the axis to a box that
          never scrolls and lets it slide away with the rows. The rounding is
          applied to the header and the row list instead. */}
      <div className="rounded-[12px] ring-1 ring-[var(--hairline)] bg-[var(--surface)]">
        {/* Axis stays put while the rows scroll — a timeline whose scale you
            cannot read is just a set of rectangles. */}
        <div className={`sticky top-0 z-10 grid ${WF_COLS} bg-[var(--surface)] border-b hairline rounded-t-[12px]`}>
          <div className="flex items-end px-3 pb-2 pt-3 border-r hairline">
            <span className="section-label">Step</span>
          </div>
          <div className="px-3 pt-3 pb-2">
            <SummaryLane root={root} gaps={gaps} total={total} />
            <div className="relative h-3 mt-1.5">
              {ticks.map((t) => <Tick key={t} t={t} total={total} />)}
            </div>
          </div>
        </div>

        <div className="relative rounded-b-[12px] overflow-hidden">
          {/* Grid lines are drawn once for the whole chart, in the track column,
              rather than per row — 3 nodes instead of 3 × every step. */}
          <div className={`pointer-events-none absolute inset-0 grid ${WF_COLS}`} aria-hidden>
            <span />
            <span className="relative mx-3">
              {ticks.slice(1, -1).map((t) => (
                <span key={t} className="absolute top-0 bottom-0 border-l border-[var(--hairline)]" style={{ left: `${t * 100}%` }} />
              ))}
            </span>
          </div>

          {rows.map(({ span, depth }) => {
            const isSel = selected === span.id;
            const isGroup = kindOf(span.kind).group;
            const dim = filter && !spanMatches(span, filter) && !span.children.some((c) => spanMatches(c, filter));
            const gap = gapBefore.get(span.id);
            return (
              <button
                key={span.id}
                data-span-id={span.id}
                onClick={() => onSelect(span.id)}
                style={{ height: WF_ROW_H }}
                className={`press relative grid ${WF_COLS} items-center w-full text-left border-b hairline last:border-b-0 ${
                  isSel ? "bg-[var(--accent-active)]" : "hover:bg-[var(--accent-hover)]"
                } ${dim ? "opacity-35" : ""}`}
              >
                {isSel && <span className="absolute left-0 inset-y-0 w-[2px] bg-[var(--text-primary)]" />}
                <span
                  className="flex items-center gap-1.5 min-w-0 h-full pr-2.5 border-r hairline"
                  /* Indent by depth, capped — past four levels the extra offset
                     costs more label width than the hierarchy it conveys. */
                  style={{ paddingLeft: 10 + Math.min(depth, 4) * 11 }}
                >
                  {span.kind === "agent" ? (
                    <PixelAvatar seed={agent.seed} hue={agent.hue} size={16} ring rounded={4} />
                  ) : (
                    <KindIcon kind={span.kind} status={span.status} tool={span.raw?.tool} size={16} />
                  )}
                  <span className={`font-mono text-[11.5px] truncate ${isGroup ? "font-medium text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}`}>
                    {span.name}
                  </span>
                  <StatusDot status={span.status} />
                  <span className="ml-auto shrink-0 font-mono text-[11px] tabular-nums text-[var(--text-quaternary)]">
                    {fmtMs(span.dur)}
                  </span>
                </span>
                <span className="relative h-full mx-3">
                  {gap && <GapRule gap={gap} span={span} total={total} />}
                  {isGroup ? <GroupBracket span={span} total={total} /> : <Bar span={span} total={total} isSel={isSel} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap mt-3">
        <LegendKey className="bg-[var(--info-bg)] ring-1 ring-[var(--info-ring)]" label="Model call" />
        <LegendKey className="bg-[var(--surface-2)] ring-1 ring-[var(--hairline-strong)]" label="Tool & other" />
        <LegendKey className="bg-[var(--success-bg)] ring-1 ring-[var(--success-ring)]" label="Verified" />
        <LegendKey className="bg-[var(--warn-bg)] ring-1 ring-[var(--warn-ring)]" label="Waiting" />
        <LegendKey className="bg-[var(--danger-bg)] ring-1 ring-[var(--danger-ring)]" label="Failed" />
        {idle > 0 && (
          <span className="text-[11px] text-[var(--text-tertiary)] ml-auto">
            {fmtMs(idle)} idle — {Math.round((idle / total) * 100)}% of the run was spent waiting, not working.
          </span>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- trace detail ------------------------------ */

/* Below lg the labels give way to the icons, and stay for screen readers. */
const VIEWS: { value: TraceView; label: ReactNode }[] = [
  { value: "tree", label: <><ListTree className="w-3 h-3" strokeWidth={1.75} /><span className="sr-only lg:not-sr-only">Tree</span></> },
  { value: "waterfall", label: <><GanttChartSquare className="w-3 h-3" strokeWidth={1.75} /><span className="sr-only lg:not-sr-only">Waterfall</span></> },
];

/** Which way of drawing a run is on screen. */
type TraceView = "tree" | "waterfall";

/* How much of the run to draw: a native <select> dressed as the strip's controls. */
function DensityPicker({ density, setDensity }: { density: DensityId; setDensity: (id: DensityId) => void }) {
  const current = DENSITY.find((d) => d[0] === density);
  return (
    <label className="relative inline-flex items-center" title={current?.[3]}>
      <SlidersHorizontal className="pointer-events-none absolute left-2 w-3 h-3 text-[var(--text-tertiary)]" strokeWidth={1.75} />
      <select
        value={density}
        onChange={(e) => setDensity(e.target.value as DensityId)}
        aria-label="Detail level"
        className="h-8 pl-[26px] pr-2.5 rounded-[9px] appearance-none cursor-pointer bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-lift text-[12px] font-medium text-[var(--text-secondary)] outline-none focus:ring-[var(--hairline-focus)] hover:text-[var(--text-primary)] transition-shadow"
      >
        {DENSITY.map(([id, label]) => (
          <option key={id} value={id}>{label}</option>
        ))}
      </select>
    </label>
  );
}

/* The metrics strip: where the run's wall-clock and money went, as label and figure
   pairs a hairline apart, each explained in its tooltip. */
function AnalyticsStrip({ overview, filter, onFilter, density, setDensity, filterRef }: {
  overview: TraceOverview;
  filter: string;
  onFilter: (q: string) => void;
  density: DensityId;
  setDensity: (id: DensityId) => void;
  filterRef: RefObject<HTMLInputElement | null>;
}) {
  const { analytics: a, idle } = overview;
  const tokens = a.tokens.input + a.tokens.output;
  return (
    <TooltipProvider delayDuration={250} skipDelayDuration={300}>
      <div className="shrink-0 flex items-center gap-1 flex-wrap px-3 py-1.5 border-b hairline">
        <Metric label="Model calls" description="Number of model calls and their combined duration. Calls running in parallel can overlap in time.">
          {a.llmCount} · {fmtMs(a.llmMs)}
        </Metric>
        <MetricRule />
        <Metric label="Tools" description="Number of tool calls and their combined duration. Calls running in parallel can overlap in time.">
          {a.toolCount} · {fmtMs(a.toolMs)}
        </Metric>
        {a.errors > 0 && (
          <>
            <MetricRule />
            <Metric label="Failed" tone="danger" description="Steps that ended in error. A run can still succeed after recovering from a failed step.">
              {a.errors}
            </Metric>
          </>
        )}
        <MetricRule />
        <Metric
          label="Tokens"
          description={<><span className="font-mono tabular-nums">{tokens.toLocaleString()}</span> tokens across model calls (<span className="font-mono tabular-nums">{a.tokens.input.toLocaleString()}</span> input + <span className="font-mono tabular-nums">{a.tokens.output.toLocaleString()}</span> output). “k” means thousand.</>}
        >
          {fmtTokens(tokens)}
        </Metric>
        {a.tokens.cached > 0 && (
          <>
            <MetricRule />
            <Metric label="Cached" description={<><span className="font-mono tabular-nums">{a.tokens.cached.toLocaleString()}</span> input tokens read from the model's prompt cache. “k” means thousand.</>}>
              {fmtTokens(a.tokens.cached)}
            </Metric>
          </>
        )}
        <MetricRule />
        <Metric label="Cost" description={<><span className="font-mono tabular-nums">{exactUsd(a.cost)}</span> in model usage for this run, in US dollars.</>}>
          {fmtCost(a.cost)}
        </Metric>
        {a.perTurn.length > 1 && a.perTurn.map((t) => (
          <React.Fragment key={t.name}>
            <MetricRule className="hidden xl:block" />
            <Metric label={t.name} description={<><span className="font-mono tabular-nums">{exactUsd(t.cost)}</span> spent in this attempt, in US dollars.</>} className="hidden xl:inline-flex">
              {fmtCost(t.cost)}
            </Metric>
          </React.Fragment>
        ))}
        {idle > 0 && (
          <>
            <MetricRule />
            <Metric
              label="Idle"
              tone="warn"
              description={<>Time in gaps between recorded steps, such as queueing, retry delays, or waiting for approval. Only gaps of at least <span className="font-mono tabular-nums">400 ms</span> are counted.</>}
            >
              {fmtMs(idle)}
            </Metric>
          </>
        )}
        <span className="flex-1" />
        <DensityPicker density={density} setDensity={setDensity} />
        {filter && (
          <RemovableChip removeLabel="Clear the step filter" onRemove={() => onFilter("")}>
            <span className="font-mono">{filter}</span>
          </RemovableChip>
        )}
        <SearchInput
          raised
          inputRef={filterRef}
          value={filter}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFilter(e.target.value)}
          placeholder="Filter steps  /"
          aria-label="Filter steps"
          className="w-[150px]"
        />
      </div>
    </TooltipProvider>
  );
}

/* When a run has stopped and is waiting on a person, that fact outranks every
   other thing on the screen — so it gets a strip of its own rather than a dot
   in a status column. It names what is being asked, and says plainly that the
   held action has not happened yet, which is the thing people actually want to
   know before they walk away from it. */
function BlockedBanner({ run, effects, onSelect }: {
  run: Run;
  effects: EffectEntry[];
  onSelect: (id: string) => void;
}) {
  const reason = blockedMeta(run.blockedReason);
  if (!reason) return null;
  const held = effects.filter((e) => e.status === "pending");
  const ask = held[0];
  return (
    <div className="shrink-0 flex items-center gap-3 px-4 py-2.5 bg-[var(--warn-bg)] border-b border-[var(--warn-ring)]">
      <span className="shrink-0 w-2 h-2 rounded-full bg-[var(--warn)]" />
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium text-[var(--warn-text)]">{reason.label}</div>
        {ask && (
          <div className="text-[11.5px] leading-[1.5] text-[var(--warn-text)] opacity-80 truncate">
            {ask.verb} · {ask.target} — nothing has happened yet
          </div>
        )}
      </div>
      {ask && (
        <button
          onClick={() => onSelect(ask.span.id)}
          className="press shrink-0 h-7 px-3 rounded-full text-[12px] font-medium bg-[var(--surface)] text-[var(--warn-text)] ring-1 ring-[var(--warn-ring)] hover:bg-[var(--accent-hover)]"
        >
          {reason.action || "Show step"}
        </button>
      )}
    </div>
  );
}

/** Who cast a vote: a teammate in platform chat, or a person using the agent (the extension). */
const VOTER = { member: "a member, in platform chat", person: "a person using the agent" };

/** What people thought of the run's answer: the ups and downs, who cast each on hover. */
function VotesChip({ votes }: { votes: ApiVote[] }) {
  if (!votes.length) return null;
  const count = (value: ApiVote["value"]) => votes.filter((vote) => vote.value === value).length;
  const who = votes
    .map(({ value, by }) => `${value === "up" ? "Good" : "Bad"} answer, said ${by.email ? `${by.email} (${VOTER[by.kind]})` : VOTER[by.kind]}`)
    .join("\n");
  return (
    <span className={chipCls} title={who} aria-label={`Votes on this answer: ${count("up")} good, ${count("down")} bad`}>
      <ThumbsUp className="w-[10px] h-[10px]" strokeWidth={1.75} /> {count("up")}
      <ThumbsDown className="w-[10px] h-[10px] ml-1" strokeWidth={1.75} /> {count("down")}
    </span>
  );
}

export default function TraceDetail({ run, root, agent, votes = [], onBack, onPrev, onNext, initialSpanId = null }: {
  run: Run;
  /** The run's tree, made from its recorded trace (`recorded.ts`). */
  root: Span;
  /** The votes people cast on the run's answer. */
  votes?: ApiVote[];
  agent: Agent;
  onBack: () => void;
  /** Null at either end of the run list. */
  onPrev?: (() => void) | null;
  onNext?: (() => void) | null;
  initialSpanId?: string | null;
}) {
  const [view, setView] = useState<TraceView>("tree");
  const [selectedId, setSelectedId] = useState(initialSpanId || "root");
  // Mobile/tablet: the inspector rail becomes a bottom sheet, opened by
  // tapping a step; the tree gets the full width.
  const { isDesktop } = useViewport();
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const selectSpan = (id: string) => {
    setSelectedId(id);
    if (!isDesktop) setInspectorOpen(true);
  };
  const [filter, setFilter] = useState("");
  const filterRef = useRef<HTMLInputElement>(null);
  /* Detail level. Balanced is the default because Compact hides the tool calls
     most people opened the trace to see, and Detailed buries them under cache
     and thinking spans. */
  const [density, setDensity] = useState<DensityId>("balanced");
  const level = densityLevel(density);
  // "N more steps" in the tree raises the level rather than expanding one row —
  // if the user wants that step, they almost always want its siblings too.
  const showMore = () =>
    setDensity((d) => DENSITY[Math.min(DENSITY.length - 1, DENSITY.findIndex((x) => x[0] === d) + 1)][0]);
  // The step panel's Fields or JSON and its Raw JSON stay as left while you move between steps.
  const [panelPrefs, setPanelPrefs] = useState<PanelPrefs>({ view: "fields", raw: false });
  // Notes pinned to spans — session-local annotations.
  const [notes, setNotes] = useState<Record<string, SpanNote[]>>({});
  const addNote = (spanId: string, text: string) =>
    setNotes((n) => ({ ...n, [spanId]: [...(n[spanId] || []), { id: `n-${(n[spanId] || []).length}-${text.length}`, text }] }));
  const deleteNote = (spanId: string, noteId: string) =>
    setNotes((n) => ({ ...n, [spanId]: (n[spanId] || []).filter((x) => x.id !== noteId) }));

  const span = findSpan(root, selectedId) || root;
  const src = SOURCE_META[run.source];

  // the run as a whole, worked out once for the metrics strip and the step panel
  const overview = useMemo((): TraceOverview => {
    // A span with nothing to bill carries no `cost` at all, so the filter is
    // "has a cost, and it is not zero".
    const items = flatten(root).map((r) => r.span).filter((s): s is BillableSpan => (s.cost as number) > 0).sort((a, b) => b.cost - a.cost);
    return {
      analytics: traceAnalytics(root), effects: effectLedger(root), idle: totalDeadAir(root),
      spend: { items, total: items.reduce((n, s) => n + s.cost, 0) },
    };
  }, [root]);
  const { effects } = overview;

  /* The rows a keyboard walk moves through — what is actually on screen, in
     draw order, so j/k never jumps to something the user cannot see. */
  const walkable = useMemo(
    () =>
      flatten(root)
        .map((r) => r.span)
        .filter((s) => passesDensity(s, level) && (!filter || spanMatches(s, filter))),
    [root, level, filter]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // A DOM event target is only an EventTarget to the type system; every
      // path below already assumes the element it came from.
      const typing = /^(INPUT|TEXTAREA)$/.test((e.target as HTMLElement)?.tagName) || (e.target as HTMLElement)?.isContentEditable;
      if (e.key === "/" && !typing) {
        e.preventDefault();
        filterRef.current?.focus();
        return;
      }
      if (e.key === "Escape" && typing) {
        (e.target as HTMLElement).blur();
        return;
      }
      if (typing || (e.target as HTMLElement)?.closest('[role="separator"]') || e.metaKey || e.ctrlKey || e.altKey) return;
      const step = e.key === "j" || e.key === "ArrowDown" ? 1 : e.key === "k" || e.key === "ArrowUp" ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      const i = walkable.findIndex((s) => s.id === selectedId);
      const next = walkable[Math.min(walkable.length - 1, Math.max(0, (i < 0 ? 0 : i) + step))];
      if (next) setSelectedId(next.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [walkable, selectedId]);

  // Keep the keyboard cursor on screen without yanking the page on a click.
  useEffect(() => {
    document
      .querySelector(`[data-span-id="${CSS.escape(selectedId)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedId, view]);

  const download = () => {
    const blob = new Blob([JSON.stringify(exportTrace(run, agent, root), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trace-${traceId(run)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported trace ${traceId(run)}`);
  };

  const navBtn =
    "press w-7 h-7 rounded-[8px] ring-1 ring-[var(--hairline-strong)] flex items-center justify-center text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)] disabled:opacity-30 disabled:pointer-events-none";

  const traceView = (
    <div className="flex-1 min-w-0 min-h-0 h-full overflow-y-auto overflow-x-hidden">
      {view === "tree" ? (
        <TreeView
          root={root} agent={agent} selected={selectedId} onSelect={selectSpan}
          filter={filter} onFilter={setFilter} level={level} onLevelUp={showMore}
        />
      ) : (
        <Waterfall root={root} agent={agent} selected={selectedId} onSelect={selectSpan} filter={filter} level={level} />
      )}
    </div>
  );
  const inspector = (
    <StepPanel
      key={span.id}
      span={span} root={root} agent={agent} run={run} notes={notes}
      onAddNote={addNote} onDeleteNote={deleteNote} onSelect={selectSpan}
      overview={overview}
      prefs={panelPrefs} onPrefs={(change) => setPanelPrefs((p) => ({ ...p, ...change }))}
    />
  );

  return (
    <div className="flex-1 min-w-0 flex flex-col min-h-0 fade-in-up">
      <PageHeader
        title={
          <>
            <button
              onClick={onBack}
              aria-label="Back to traces"
              className="press shrink-0 flex items-center gap-1 h-7 pl-1.5 pr-1.5 sm:pr-2.5 rounded-[8px] text-[12.5px] font-medium text-[var(--text-secondary)] ring-1 ring-[var(--hairline-strong)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
            >
              <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span className="hidden sm:inline">Traces</span>
            </button>
            <div className="min-w-0 hidden sm:flex items-center gap-2.5">
              <span className="text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.005em] truncate">
                {run.title}
              </span>
              <span className="hidden lg:inline-flex"><CopyId id={traceId(run)} /></span>
            </div>
            <span className="hidden sm:inline-flex"><StatusPill status={run.status} /></span>
            <span className={`${chipCls} hidden lg:inline-flex`}>
              <src.Icon className="w-[10px] h-[10px]" strokeWidth={1.75} />
              {src.label}
            </span>
            {run.recorded?.recipe_run && <RecipeChip recipe={run.recorded.recipe_run} className="hidden lg:inline-flex" />}
            {/* Between sm and lg the title needs the room; the id and duration are on the list row and the root step */}
            <span className="hidden lg:inline-flex"><DurChip ms={root.dur} /></span>
            {/* at every width: on a phone it is the one thing here the list row doesn't say */}
            <VotesChip votes={votes} />
          </>
        }
      >
        <button onClick={onPrev as () => void} disabled={!onPrev} title="Previous run" className={navBtn}>
          <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
        <button onClick={onNext as () => void} disabled={!onNext} title="Next run" className={navBtn}>
          <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
        <button onClick={download} title="Export trace as JSON" className={navBtn}>
          <Download className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
        <Segmented role="radiogroup" label="Draw the run as" options={VIEWS} value={view} onChange={setView} />
      </PageHeader>

      {/* A phone's header has room only for the controls; the run gets its own line */}
      <div className="sm:hidden shrink-0 flex items-start gap-2 px-4 py-2.5 border-b hairline">
        <span className="min-w-0 flex-1 break-words text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.005em]">{run.title}</span>
        <StatusPill status={run.status} />
      </div>
      {/* below lg the header has no room: the recipe gets a line of its own */}
      {run.recorded?.recipe_run && (
        <div className="lg:hidden shrink-0 flex px-4 py-2 border-b hairline">
          <RecipeChip recipe={run.recorded.recipe_run} />
        </div>
      )}

      <AnalyticsStrip
        overview={overview}
        filter={filter}
        onFilter={setFilter}
        density={density}
        setDensity={setDensity}
        filterRef={filterRef}
      />

      <BlockedBanner run={run} effects={effects} onSelect={selectSpan} />

      <div className="flex-1 min-h-0 flex">
        {isDesktop ? (
          <ResizablePanelGroup direction="horizontal" autoSaveId="trace-inspector" className="min-h-0">
            <ResizablePanel id="trace" defaultSize={60} minSize={35}>
              {traceView}
            </ResizablePanel>
            <ResizableHandle
              aria-label="Resize step details"
              title="Drag to resize step details"
              className="bg-[var(--hairline)] before:absolute before:h-8 before:w-1 before:rounded-full before:bg-[var(--hairline-strong)] hover:before:bg-[var(--text-quaternary)] data-[resize-handle-state=drag]:before:bg-[var(--text-tertiary)] focus-visible:ring-[var(--hairline-focus)] focus-visible:ring-offset-0"
            />
            <ResizablePanel id="inspector" defaultSize={40} minSize={35} maxSize={65}>
              {inspector}
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          <>
            {traceView}
            <BottomSheet open={inspectorOpen} onOpenChange={setInspectorOpen}>
              <div className="h-full">{inspector}</div>
            </BottomSheet>
          </>
        )}
      </div>
    </div>
  );
}

/** The recipe the run ran: its name and version, the item and step the page refused when one
    did — the site changed — and the version the agent fixed it to in the same run. */
function RecipeChip({ recipe, className }: { recipe: RunRecipe; className?: string }) {
  const version = recipe.version ? ` · v${recipe.version}` : "";
  const missed = recipe.missed ? `item ${recipe.missed.item}, step ${recipe.missed.step} missed` : null;
  const fixed = recipe.updated_to ? `, fixed to v${recipe.updated_to}` : "";
  return (
    <span
      className={cn(chipCls, "min-w-0", missed && "bg-[var(--warn-bg)] ring-[var(--warn-ring)] text-[var(--warn-text)]", className)}
      title={`Ran the recipe ${recipe.name}${version}: ${missed ? `${missed}, the site changed${fixed}` : recipe.done ? "every step done" : "not every step done"}`}
    >
      {/* a long name gives way; the missed step is the point, so it never does */}
      <span className="min-w-0 truncate">{recipe.name}{version}</span>
      {missed && <span className="shrink-0">{` · ${missed}${fixed}`}</span>}
    </span>
  );
}
